import { AlertTriangle } from "lucide-react";

import { ErrorAlert, Badge, Button, Breadcrumb, Section } from "@/components/ui";
import type { Diagnosis } from "@/lib/domains";
import { Heading, HeadTile, DomainPage, RuleRow, Notice, RecordChip } from "@/components/domains/parts";

type DnsRow = Diagnosis["records"][number];

function checkedAgo(at: number, now = Date.now()): string {
  const minutes = Math.floor((now - at) / 60_000);
  if (minutes < 1) return "just now";
  return minutes === 1 ? "a minute ago" : `${minutes} minutes ago`;
}

/** Figma "One record still needs fixing" (2081-411): what we asked for vs what the registrar has. */
export function RecordIssue({
  hostname,
  records,
  checkedAt,
  checking,
  error,
  onRecheck,
  onShowAll,
}: {
  hostname: string;
  records: DnsRow[];
  checkedAt: number;
  checking: boolean;
  error: unknown;
  onRecheck: () => void;
  onShowAll: () => void;
}) {
  const wrong = records.filter((r) => r.matches === false);
  const others = records.filter((r) => r.matches !== false);
  const one = wrong.length === 1;
  const allDiffer = wrong.every((r) => r.found.length > 0);

  return (
    <DomainPage>
      <Breadcrumb
        items={[
          { label: "Domains", to: "/domains" },
          { label: "...", to: "/domains" },
          { label: hostname },
          { label: "record issue" },
        ]}
      />
      <Heading
        size={24}
        tile={
          <HeadTile tone="danger">
            <AlertTriangle className="size-5" aria-hidden />
          </HeadTile>
        }
        title={one ? "One record still needs fixing" : `${wrong.length} records still need fixing`}
        subtitle={`${hostname} · we checked ${checkedAgo(checkedAt)}`}
      />
      <Notice
        tone="danger"
        title={one ? `The ${wrong[0]!.type} record doesn't match yet` : "Some records don't match yet"}
      >
        {allDiffer
          ? "We found a different value at your registrar than what we asked for. Update it exactly as shown below, then recheck — this can take a few minutes to take effect."
          : "We couldn't find everything we asked for at your registrar yet. Add the records exactly as shown below, then recheck — this can take a few minutes to take effect."}
      </Notice>

      <Section title="What we expected vs. what we found">
        {wrong.map((r, i) => (
          <div
            key={`${r.type}-${r.host}-${r.value}`}
            className={
              i > 0 ? "grid gap-4 border-t border-border pt-4 md:grid-cols-2" : "grid gap-4 md:grid-cols-2"
            }
          >
            <div className="flex min-w-0 flex-col gap-2">
              <p className="text-[11px] font-semibold tracking-[0.2px] text-[#007955] uppercase">Expected</p>
              <RecordChip type={r.type} host={r.host} value={r.value} />
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <p className="text-[11px] font-semibold tracking-[0.2px] text-[#b52522] uppercase">
                Found at your registrar
              </p>
              {r.found.length > 0 ? (
                r.found.map((v) => <RecordChip key={v} type={r.type} host={r.host} value={v} />)
              ) : (
                <RecordChip type={r.type} host={r.host} value="Nothing found yet" />
              )}
            </div>
          </div>
        ))}
        <ErrorAlert error={error} />
        <div className="flex flex-wrap gap-2.5">
          <Button variant="brand" size="lg" onClick={onRecheck} loading={checking}>
            Recheck now
          </Button>
          <Button size="lg" variant="secondary" onClick={onShowAll}>
            View all instructions
          </Button>
        </div>
      </Section>

      {others.length > 0 && (
        <Section title="Other records">
          {others.map((r, i) => (
            <RuleRow key={`${r.type}-${r.host}-${r.value}`} first={i === 0}>
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-[#5c5551]">
                {r.type} {r.host}
              </p>
              {r.matches ? (
                <Badge size="md" tone="success">
                  Verified
                </Badge>
              ) : (
                <Badge size="md" tone="neutral">
                  Not checked
                </Badge>
              )}
            </RuleRow>
          ))}
        </Section>
      )}
    </DomainPage>
  );
}
